import React from 'react';
import { BaseActivityMessage } from '../BaseActivityMessage';
import { TimePicker } from '../TimePicker';

export interface TimeActivityMessageProps {
  /**
   * Current time value (HH:mm format)
   */
  value: string;
  /**
   * Callback when time changes
   */
  onChange: (value: string | null) => void;
  /**
   * Callback when submit button is clicked
   */
  onSubmit: () => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Minimum allowed time (ISO string)
   */
  minTime?: string;
  /**
   * Maximum allowed time (ISO string)
   */
  maxTime?: string;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Label for the time picker
   */
  label?: string;
  /**
   * Minutes step interval
   */
  minutesStep?: number;
  /**
   * Whether the submit button should be enabled
   */
  submitEnabled?: boolean;
  /**
   * Hide the submit button (used for locked / read-only rendering).
   */
  hideSubmit?: boolean;
  /**
   * Message content to display above the time picker
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
  /**
   * Custom bot display name
   */
  botName?: string;
  /**
   * Optional content rendered below the prompt bubble (e.g. an AI-translation disclosure hint).
   */
  footer?: React.ReactNode;
}

/**
 * TimeActivityMessage displays a time selection interface with submit and optional skip buttons.
 * This component is designed for interactive time selection in chat-like interfaces,
 * with support for time constraints and custom validation.
 */
export const TimeActivityMessage: React.FC<TimeActivityMessageProps> = ({
  value,
  onChange,
  onSubmit,
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Send',
  onSkip,
  minTime,
  maxTime,
  disabled = false,
  label = '',
  minutesStep = 15,
  submitEnabled = true,
  hideSubmit = false,
  children,
  sender,
  avatarUrl,
  timestamp,
  botName,
  footer,
}) => {
  return (
    <BaseActivityMessage
      allowSkip={allowSkip}
      skipText={skipText}
      submitText={submitText}
      onSkip={onSkip}
      disabled={disabled}
      submitEnabled={submitEnabled}
      hideSubmit={hideSubmit}
      sender={sender}
      avatarUrl={avatarUrl}
      timestamp={timestamp}
      botName={botName}
      footer={footer}
      onSubmit={onSubmit}
      renderInput={() => (
        <TimePicker
          value={value}
          onChange={onChange}
          maxTime={maxTime}
          minTime={minTime}
          disabled={disabled || sender === 'user'}
          label={label}
          minutesStep={minutesStep}
        />
      )}
    >
      {children}
    </BaseActivityMessage>
  );
};
